import React, { useState, useEffect } from 'react'
import { rhuService } from '../../../../services/rhuService'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  People as PeopleIcon
} from '@mui/icons-material';

const RHUReports = () => {
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedBarangay, setSelectedBarangay] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [serviceSummary, setServiceSummary] = useState([])
  const [selectedService, setSelectedService] = useState(null)
  const [showPatientModal, setShowPatientModal] = useState(false)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const barangays = ['All', 'Bakhawan', 'Calabasahan', 'Dalajican', 'Masadya', 'Masudsud', 'Poblacion', 'San Pedro', 'San Vicente', 'Sampong']
  const months = ['All', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString())

  useEffect(() => {
    loadOrders()
  }, [])

  useEffect(() => {
    filterOrders()
  }, [orders, selectedBarangay, selectedMonth, selectedYear])

  const loadOrders = async () => {
    setLoading(true)
    const result = await rhuService.getAllOrders()
    if (result.success) {
      setOrders(result.data)
    }
    setLoading(false)
  }

  const filterOrders = () => {
    let filtered = [...orders]

    // Filter by barangay
    if (selectedBarangay && selectedBarangay !== 'All') {
      filtered = filtered.filter(order =>
        order.patient?.address?.toLowerCase().includes(selectedBarangay.toLowerCase())
      )
    }

    // Filter by month and year
    if (selectedMonth && selectedMonth !== 'All') {
      const monthIndex = months.indexOf(selectedMonth) - 1 // -1 because 'All' is first
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at)
        return orderDate.getMonth() === monthIndex
      })
    }

    // Filter by year
    if (selectedYear) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at)
        return orderDate.getFullYear().toString() === selectedYear
      })
    }

    setFilteredOrders(filtered)
    calculateServiceSummary(filtered)
  }

  const calculateServiceSummary = (orderList) => {
    const serviceMap = {}
    
    console.log('Calculating service summary for orders:', orderList.length)

    orderList.forEach(order => {
      if (order.items && order.items.length > 0) {
        console.log('Order items:', order.items)
        console.log('Sample item:', order.items[0], 'Unit Price:', order.items[0]?.unit_price, 'Quantity:', order.items[0]?.quantity)
        order.items.forEach(item => {
          const serviceKey = item.service_name
          if (!serviceMap[serviceKey]) {
            serviceMap[serviceKey] = {
              name: item.service_name,
              category: item.category || 'General',
              count: 0,
              patientSet: new Set(),
              patients: [],
              totalAmount: 0
            }
          }
          serviceMap[serviceKey].count += item.quantity || 0
          const itemAmount = parseFloat(item.unit_price || 0) * parseInt(item.quantity || 0)
          serviceMap[serviceKey].totalAmount += itemAmount
          
          if (order.patient?.name) {
            const patientKey = `${order.patient.name}-${order.patient.id}`
            if (!serviceMap[serviceKey].patientSet.has(patientKey)) {
              serviceMap[serviceKey].patientSet.add(patientKey)
              serviceMap[serviceKey].patients.push({
                name: order.patient.name,
                age: calculateAge(order.patient.birthdate),
                address: order.patient.address,
                orderNumber: order.order_number,
                date: order.created_at,
                amount: itemAmount
              })
            }
          }
        })
      }
    })

    const summary = Object.values(serviceMap).map(service => ({
      name: service.name,
      category: service.category,
      count: service.count,
      patientCount: service.patients.length,
      patients: service.patients,
      totalAmount: service.totalAmount
    })).sort((a, b) => b.patientCount - a.patientCount)

    console.log('Service summary:', summary)
    setServiceSummary(summary)
  }

  const calculateAge = (birthdate) => {
    if (!birthdate) return 'N/A'
    const today = new Date()
    const birth = new Date(birthdate)
    let age = today.getFullYear() - birth.getFullYear()
    const monthDiff = today.getMonth() - birth.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--
    }
    return age
  }

  const getTotalPatients = () => {
    const uniquePatients = new Set(filteredOrders.map(o => o.patient?.name).filter(Boolean))
    return uniquePatients.size
  }

  const getTotalServices = () => {
    return filteredOrders.reduce((total, order) => {
      return total + (order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0)
    }, 0)
  }

  const getTotalAmount = () => {
    return filteredOrders.reduce((total, order) => total + parseFloat(order.total_amount || 0), 0)
  }

  const handleServiceClick = (service) => {
    setSelectedService(service)
    setShowPatientModal(true)
  }

  const handlePrintServicesSummary = () => {
    window.print()
  }

  const handlePrintServicePatients = (service) => {
    const printWindow = window.open('', '_blank')
    const patientRows = service.patients.map(patient => `
      <tr>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">${patient.name}</td>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">${patient.age} years</td>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">${patient.address}</td>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">${patient.orderNumber}</td>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">₱${parseFloat(patient.amount || 0).toFixed(2)}</td>
        <td style="padding: 0.75rem; border-bottom: 1px solid #e5e7eb;">${new Date(patient.date).toLocaleDateString()}</td>
      </tr>
    `).join('')
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Patient List - ${service.name}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 2rem;
              max-width: 1200px;
              margin: 0 auto;
            }
            h1 {
              color: #374151;
              margin-bottom: 0.5rem;
            }
            .meta {
              color: #6b7280;
              margin-bottom: 2rem;
              display: flex;
              gap: 1rem;
              flex-wrap: wrap;
            }
            .badge {
              background: #dbeafe;
              color: #1e40af;
              padding: 0.25rem 0.75rem;
              border-radius: 6px;
              font-size: 0.875rem;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 1rem;
            }
            th {
              background: #f3f4f6;
              padding: 0.75rem;
              text-align: left;
              font-weight: 600;
              border-bottom: 2px solid #e5e7eb;
            }
            .total {
              font-weight: 700;
              font-size: 1.1rem;
              margin-top: 1rem;
              text-align: right;
              color: #667eea;
            }
          </style>
        </head>
        <body>
          <h1>Patient List - ${service.name}</h1>
          <div class="meta">
            <span class="badge">${service.category}</span>
            <span>Total Patients: ${service.patientCount}</span>
            <span>Total Amount: ₱${parseFloat(service.totalAmount || 0).toFixed(2)}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Age</th>
                <th>Address</th>
                <th>Order #</th>
                <th>Amount</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${patientRows}
            </tbody>
          </table>
          <div class="total">Total Amount: ₱${parseFloat(service.totalAmount || 0).toFixed(2)}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @media (max-width: 768px) {
          .table-container {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .table {
            font-size: 0.875rem;
          }
          .table th, .table td {
            padding: 0.5rem !important;
            white-space: nowrap;
          }
        }
        
        @media (max-width: 480px) {
          .table {
            font-size: 0.75rem;
          }
          .table th, .table td {
            padding: 0.375rem !important;
          }
        }
        
        @media print {
          .no-print {
            display: none !important;
          }
          .page-header, .filters-section, .patient-table-section {
            display: none !important;
          }
          .service-summary-section {
            page-break-inside: avoid;
          }
        }
      `}</style>

      <div className="section" style={{ padding: '1.5rem 1rem' }}>
        <div className="container" style={{ maxWidth: '1400px' }}>
          {/* Header */}
          <div className="page-header" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '2rem',
            color: 'white',
            animation: 'fadeInUp 0.3s ease'
          }}>
            <h1 className="title is-3" style={{ color: 'white', marginBottom: '0.5rem' }}>
              <i className="fas fa-chart-bar" style={{ marginRight: '0.75rem' }}></i>
              Reports & Analytics
            </h1>
            <p style={{ opacity: 0.9, fontSize: '1rem' }}>
              View patient records, service utilization, and generate reports
            </p>
          </div>

          {/* Filters Section */}
          <div className="box filters-section" style={{
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginBottom: '2rem',
            animation: 'fadeInUp 0.3s ease 0.4s both'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem', 
              alignItems: 'flex-end',
              flexWrap: 'wrap'
            }}>
              {/* Barangay Filter */}
              <div className="field" style={{ flex: '0 1 200px', minWidth: '150px' }}>
                <label className="label" style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Barangay</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select 
                      value={selectedBarangay}
                      onChange={(e) => setSelectedBarangay(e.target.value)}
                      style={{ borderRadius: '10px' }}
                    >
                      {barangays.map(brgy => (
                        <option key={brgy} value={brgy === 'All' ? '' : brgy}>{brgy}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Month Filter */}
              <div className="field" style={{ flex: '0 1 180px', minWidth: '140px' }}>
                <label className="label" style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Month</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select 
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      style={{ borderRadius: '10px' }}
                    >
                      {months.map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Year Filter */}
              <div className="field" style={{ flex: '0 1 140px', minWidth: '120px' }}>
                <label className="label" style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.5rem' }}>Year</label>
                <div className="control">
                  <div className="select is-fullwidth">
                    <select 
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      style={{ borderRadius: '10px' }}
                    >
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="button is-primary"
                  onClick={loadOrders}
                  style={{ borderRadius: '10px' }}
                >
                  <span className="icon">
                    <i className="fas fa-sync-alt"></i>
                  </span>
                  <span>Refresh</span>
                </button>
                <button 
                  className="button is-light"
                  onClick={() => {
                    setSelectedBarangay('')
                    setSelectedMonth('')
                    setSelectedYear(new Date().getFullYear().toString())
                  }}
                  style={{ borderRadius: '10px' }}
                >
                  <span className="icon">
                    <i className="fas fa-redo"></i>
                  </span>
                  <span>Clear Filters</span>
                </button>
              </div>
            </div>
          </div>

          {/* Service Summary Cards */}
          <div className="box service-summary-section" style={{
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            marginBottom: '2rem',
            animation: 'fadeInUp 0.3s ease 0.5s both'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <div>
                <h2 className="title is-5" style={{ margin: 0, color: '#374151' }}>
                  <i className="fas fa-chart-pie" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                  Services Summary - Patient Count
                </h2>
                <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                  Number of unique patients who availed each service
                </p>
              </div>
              <button 
                className="button is-info no-print"
                onClick={handlePrintServicesSummary}
                style={{ borderRadius: '10px' }}
              >
                <span className="icon">
                  <i className="fas fa-print"></i>
                </span>
                <span>Print</span>
              </button>
            </div>

            {serviceSummary.length > 0 ? (
              <>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                  gap: '0.5rem' 
                }}>
                  {serviceSummary.map((service, index) => (
                    <div 
                      key={index} 
                      onClick={() => handleServiceClick(service)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem 1rem',
                        background: index % 2 === 0 ? '#f9fafb' : 'white',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#eef2ff'
                        e.currentTarget.style.borderColor = '#667eea'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = index % 2 === 0 ? '#f9fafb' : 'white'
                        e.currentTarget.style.borderColor = '#e5e7eb'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }}
                      className="no-print"
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontWeight: '600', 
                          fontSize: '0.95rem', 
                          color: '#374151',
                          marginBottom: '0.25rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          {service.name}
                          <i className="fas fa-external-link-alt" style={{ fontSize: '0.7rem', color: '#667eea' }}></i>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                          {service.category}
                        </div>
                      </div>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        paddingLeft: '1rem',
                        flexShrink: 0
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <i className="fas fa-users" style={{ color: '#667eea', fontSize: '1rem' }}></i>
                          <span style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700', 
                            color: '#667eea'
                          }}>
                            {service.patientCount}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                            patients
                          </span>
                        </div>
                        <div style={{ 
                          borderLeft: '2px solid #e5e7eb',
                          paddingLeft: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end'
                        }}>
                          <span style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase' }}>
                            Amount
                          </span>
                          <span style={{ 
                            fontSize: '1.1rem', 
                            fontWeight: '700', 
                            color: '#10b981'
                          }}>
                            ₱{(service.totalAmount || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem 1rem', 
                  background: '#eef2ff', 
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <i className="fas fa-info-circle" style={{ color: '#667eea', marginRight: '0.5rem' }}></i>
                  <span style={{ color: '#4c51bf', fontSize: '0.85rem', fontWeight: '500' }}>
                    Total: {serviceSummary.length} services
                  </span>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                <i className="fas fa-chart-pie" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
                <p>No service data available for the selected filters</p>
              </div>
            )}
          </div>

          {/* Patient List */}
          <div className="box patient-table-section" style={{
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            animation: 'fadeInUp 0.3s ease 0.6s both'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <h2 className="title is-5" style={{ margin: 0, color: '#374151' }}>
                <i className="fas fa-list" style={{ marginRight: '0.5rem', color: '#667eea' }}></i>
                Patient Records ({filteredOrders.length})
              </h2>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#667eea' }}></i>
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading records...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
                <p style={{ marginTop: '1rem', color: '#6b7280' }}>No records found</p>
              </div>
            ) : (
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="table is-fullwidth is-hoverable" style={{ background: 'white' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th>Order #</th>
                      <th>Patient Name</th>
                      <th>Age</th>
                      <th>Address</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id} style={{
                        animation: `fadeInUp 0.3s ease ${index * 0.05}s both`
                      }}>
                        <td style={{ fontWeight: '600', color: '#667eea' }}>{order.order_number}</td>
                        <td style={{ fontWeight: '600' }}>{order.patient?.name || 'N/A'}</td>
                        <td>{calculateAge(order.patient?.birthdate)} years</td>
                        <td>{order.patient?.address || 'N/A'}</td>
                        <td style={{ fontWeight: '700', color: '#667eea' }}>
                          ₱{parseFloat(order.total_amount).toFixed(2)}
                        </td>
                        <td>
                          <span className={`tag ${order.status === 'paid' ? 'is-success' : 'is-warning'}`} style={{ borderRadius: '6px' }}>
                            {order.status}
                          </span>
                        </td>
                        <td>{new Date(order.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Patient List Modal */}
      <Dialog
        open={showPatientModal && !!selectedService}
        onClose={() => setShowPatientModal(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        scroll="paper"
      >
        {selectedService && (
          <>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon />
                <Typography variant="h6">Patients for {selectedService.name}</Typography>
              </Box>
              <IconButton onClick={() => setShowPatientModal(false)} sx={{ color: 'white' }} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            
            <DialogContent dividers sx={{ p: 0 }}>
              <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip 
                        label={selectedService.category} 
                        color="info" 
                        variant="outlined" 
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Total: {selectedService.patientCount} patients
                      </Typography>
                      <Typography variant="subtitle1" color="success.main" fontWeight="bold">
                        ₱{(selectedService.totalAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Button 
                      variant="contained" 
                      color="info" 
                      size="small"
                      startIcon={<PrintIcon />}
                      onClick={() => handlePrintServicePatients(selectedService)}
                    >
                      Print
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer sx={{ maxHeight: '60vh' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient Name</TableCell>
                      <TableCell>Age</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Order #</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedService.patients.map((patient, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 'medium' }}>{patient.name}</TableCell>
                        <TableCell>{patient.age} years</TableCell>
                        <TableCell>{patient.address}</TableCell>
                        <TableCell sx={{ color: 'primary.main', fontWeight: 'medium' }}>{patient.orderNumber}</TableCell>
                        <TableCell sx={{ color: 'success.main', fontWeight: 'medium' }}>
                          ₱{parseFloat(patient.amount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{new Date(patient.date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ p: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">TOTAL:</Typography>
                  <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                    ₱{(selectedService.totalAmount || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setShowPatientModal(false)} variant="outlined">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default RHUReports
